import React from 'react';
import { 
  Calendar, 
  User, 
  Package, 
  DollarSign, 
  Mail, 
  Printer, 
  Trash2, 
  Check, 
  Clipboard,
  Download,
  Clipboard as CopyIcon,
  QrCode
} from 'lucide-react';
import { formatCurrency, getMinutesAgo } from '../Utils/formatters';
import { generateGunOrderReceipt } from '../Utils/printUtils';
import { extractDogTagCSV } from '../Utils/printUtils';

const OrderCard = ({
  orderKey,
  order,
  isExpanded,
  onToggleExpand,
  onDelete,
  onEmail,
  onPrint,
  onCopyUrl,
  processing,
  status,
  emailedKeys,
  printedKeys,
  onUpdateEmail,
  onUpdateNotes,
  onShowQRCode
}) => {
  const handleEmail = () => {
    if (order.type === 'photo' && order.email && order.url) {
      onEmail(orderKey, order.email, order.url);
    }
  };

  const handleShowQRCode = () => {
    if (order.type === 'photo' && order.url) {
      onShowQRCode(order.url);
    }
  };

  const handlePrint = () => {
    if (order.type === 'gun') {
      const receiptHtml = generateGunOrderReceipt(order, orderKey);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 100);
      };

      iframe.srcdoc = receiptHtml;
      onPrint(orderKey);
    }
  };

  const handleDogTagPrint = () => {
    const csvData = extractDogTagCSV(order);
    if (csvData) {
      alert(csvData);
    }
  };

  const handleDownloadAllItems = async () => {
    try {
      if (!('showDirectoryPicker' in window)) {
        window.alert('Directory picker not supported. Please use Chrome/Edge to download files.');
        return;
      }

      const directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads'
      });

      for (const itemUrl of order.items) {
        try {
          const response = await fetch(itemUrl);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          const blob = await response.blob();
          const fileName = itemUrl.split('/').pop() || 'download';
          
          const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
        } catch (error) {
          console.error(`Failed to download ${itemUrl}:`, error);
        }
      }
      
      window.alert(`Downloaded ${order.items.length} files to selected directory`);
    } catch (error) {
      console.error('Download failed:', error);
      window.alert('Download failed. See console for details.');
    }
  };

  const renderOrderItems = () => {
    if (order.type === 'gun') {
      return (
        <div className="items-list">
          {order.items.map((item, i) => (
            <div key={i} className="gun-item">
              • {item}
            </div>
          ))}
        </div>
      );
    }

    if (order.type === 'photo' && isExpanded) {
      return (
        <div className="items-list">
          {order.items.map((itemUrl, i) => {
            const low = itemUrl.toLowerCase();
            const isImg = /\.(png|jpe?g|gif|webp|bmp)$/i.test(low);
            const isVid = /\.(mp4|webm|ogg|mov)$/i.test(low);
            return (
              <div key={i} className="item-preview-container">
                <div className="item-preview-wrapper">
                  {isImg ? (
                    <img
                      src={itemUrl}
                      alt={`Item ${i + 1}`}
                      className="item-preview"
                    />
                  ) : isVid ? (
                    <video
                      src={itemUrl}
                      controls
                      className="item-preview"
                    />
                  ) : (
                    <a
                      href={itemUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {itemUrl}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="order-card" data-type={order.type}>
      <div
        className="order-header accordion-header"
        onClick={() => onToggleExpand(orderKey)}
      >
        <div className="header-content">
          <div className="admin-order-summary">
            <div className="order-date">
              <Calendar size={16} />
              <span className="minutes-elapsed">
                {getMinutesAgo(order.timestamp)}
              </span>
            </div>

            <div className="customer-info">
              <User size={16} />
              {order.type === "photo" ? (
                <div className="customer-email">{order.email || "No email"}</div>
              ) : (
                <div className="customer-name">{order.customer}</div>
              )}
            </div>
          </div>

          <div className="header-right">
            <div className="order-type-label">
              {order.type === "gun" ? "ORDER" : "MEDIA ORDER"}
            </div>
            <div className={`order-type-label status-${status.toLowerCase()}`}>
              {status}
            </div>
            <div className="order-total">
              {order.total === 0 ? "Free" : formatCurrency(order.total)}
            </div>
            <button
              className={`status-btn ${status !== "New" ? "completed" : "delete"}`}
              disabled={processing?.delete}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(orderKey);
              }}
            >
              {processing?.delete ? (
                <span className="spin">⟳</span>
              ) : status !== "New" ? (
                <Check size={16} />
              ) : (
                <Trash2 size={16} />
              )}
            </button>
          </div>
        </div>

      </div>

      {isExpanded && (
        <div className="order-details">
          <div className="order-notes">
            <Clipboard size={16} />
            <textarea
              className="notes-textarea"
              value={order.notes || ''}
              onChange={(e) => onUpdateNotes(orderKey, e.target.value)}
              placeholder="Add notes for this order..."
              rows={3}
            />
          </div>

          <div className="metrics">
            <div className="metric">
              <div className="metric-label">
                <Package size={16} /> Items
              </div>
              <div className="metric-value">{order.items.length}</div>
            </div>
            <div className="metric metric-total">
              <div className="metric-label">
                <DollarSign size={16} /> Total
              </div>
              <div className="metric-value">
                {order.total === 0 ? "Free" : formatCurrency(order.total)}
              </div>
            </div>
          </div>

          <div className="actions">
            {order.type === "photo" ? (
              <>
                <button
                  className="btn btn-info"
                  disabled={processing?.email}
                  onClick={handleEmail}
                >
                  {processing?.email ? (
                    <span className="spin">⟳</span>
                  ) : (
                    <Mail size={16} />
                  )}
                  {processing?.email
                    ? "Sending…"
                    : emailedKeys.has(orderKey)
                    ? "Sent"
                    : "Send Email"}
                </button>

                <button
                  className="btn btn-success"
                  onClick={() => window.open(order.url, "_blank")}
                >
                  <Download size={16} /> Download Zip File
                </button>

                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadAllItems();
                  }}
                >
                  <Download size={16} /> Download All Files
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() => onCopyUrl(order.url)}
                >
                  <CopyIcon size={16} /> Copy Zip Link URL
                </button>

                <button
                  className="btn btn-info"
                  onClick={handleShowQRCode}
                >
                  <QrCode size={16} /> Show QR Code
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handlePrint}
                >
                  <Printer size={16} /> Print Order
                </button>

                {order.items.some(item => item.includes('Dog Tag:')) && (
                  <button
                    className="btn btn-info"
                    onClick={handleDogTagPrint}
                  >
                    <Clipboard size={16} /> Print Dogtag
                  </button>
                )}
              </>
            )}

            <button
              className={status !== "New" ? "btn btn-success" : "btn btn-danger"}
              disabled={processing?.delete}
              onClick={() => onDelete(orderKey)}
            >
              {processing?.delete ? (
                <span className="spin">⟳</span>
              ) : status !== "New" ? (
                <Check size={16} />
              ) : (
                <Trash2 size={16} />
              )}
            </button>
          </div>

          {renderOrderItems()}
        </div>
      )}
    </div>
  );
};

export default OrderCard;